import { useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { alterarTexto, listarTextos } from '../../textos/api/textos';
import type { TextoDaTela } from '../../textos/api/textos';
import { mensagemDaFalha } from './comum';
import type { Aviso } from './comum';

const vigente = (texto: TextoDaTela) => texto.valor ?? texto.padrao;
const semAcento = (valor: string) => valor.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function mensagemDoTexto(erro: unknown): string {
    if (erro instanceof ErroRespostaHttp && erro.corpo?.codigo === 'valor_desatualizado') {
        return 'Um texto mudou no banco desde que a tela foi carregada. A lista foi relida: confira e salve de novo.';
    }
    return mensagemDaFalha(erro);
}

/**
 * A aba Textos de Configurações, no padrão das fases: seletor de tela (e a busca em todas), a tela
 * marcada com os textos dela, e tudo pendente até "Salvar Alterações". "Restaurar original" também
 * fica pendente: vai como `valor: null`. A alteração aparece no tablet quando o aplicativo reabre.
 */
export function TextosConfiguracao({ csrfToken }: { csrfToken: string }) {
    const [textos, setTextos] = useState<TextoDaTela[] | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);
    /** O digitado por chave; `null` é "restaurar o original" no salvar. */
    const [rascunho, setRascunho] = useState<Record<string, string | null>>({});
    const [tela, setTela] = useState<string | null>(null);
    const [busca, setBusca] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            setTextos(await listarTextos());
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar os textos.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!textos) return <p className="estado-variaveis">Carregando os textos…</p>;
    if (textos.length === 0) {
        return (
            <p className="aviso-fase-aberta" role="status">
                Nenhum texto cadastrado. Aplique as migrações do servidor (<code>alembic upgrade head</code>) para criar a lista.
            </p>
        );
    }

    const telas = [...new Set(textos.map((texto) => texto.tela))];
    const marcada = tela && telas.includes(tela) ? tela : telas[0];
    const termo = semAcento(busca.trim());
    const visiveis = textos.filter((texto) => (termo
        ? [texto.rotulo, texto.padrao, texto.valor ?? '', texto.chave].some((campo) => semAcento(campo).includes(termo))
        : texto.tela === marcada));

    const exibido = (texto: TextoDaTela) => {
        const digitado = rascunho[texto.chave];
        if (digitado === null) return texto.padrao;
        return digitado ?? vigente(texto);
    };
    /** O que vai ao servidor para este texto, ou `undefined` quando nada mudou. */
    const pendente = (texto: TextoDaTela): string | null | undefined => {
        const digitado = rascunho[texto.chave];
        if (digitado === undefined) return undefined;
        if (digitado === null) return texto.valor === null ? undefined : null;
        return digitado === vigente(texto) ? undefined : digitado;
    };
    const alteradosNaTela = (nome: string) => textos.filter((texto) => texto.tela === nome && texto.valor !== null).length;

    async function salvar() {
        if (!textos) return;
        setAviso(null);
        const mudancas = textos.flatMap((texto) => {
            const valor = pendente(texto);
            return valor === undefined ? [] : [{ texto, valor }];
        });
        // Variável `{assim}` que some quebra a tela do tablet: barra antes de qualquer gravação.
        for (const { texto, valor } of mudancas) {
            const falta = valor === null ? undefined : texto.variaveis.find((variavel) => !valor.includes(variavel));
            if (falta) {
                if (!termo) setTela(texto.tela);
                setAviso({ tipo: 'erro', texto: `«${texto.rotulo}» precisa manter ${falta}.` });
                return;
            }
        }
        setSalvando(true);
        try {
            for (const { texto, valor } of mudancas) await alterarTexto(texto.chave, valor, texto.valor, csrfToken);
            setRascunho({});
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas. Os tablets passam a mostrar ao reabrir o aplicativo.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDoTexto(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    const tituloDoBloco = termo ? 'Resultado da busca' : marcada;

    return (
        <div className="config-fase">
            <div className="seletor-enigma-config">
                <label>
                    <span>Tela:</span>
                    <select aria-label="Tela" value={marcada} disabled={Boolean(termo)} onChange={(evento) => setTela(evento.target.value)}>
                        {telas.map((nome) => (
                            <option key={nome} value={nome}>{nome}</option>
                        ))}
                    </select>
                </label>
                <input
                    className="busca-config"
                    type="search"
                    placeholder="Buscar em todas as telas"
                    aria-label="Buscar texto"
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                />
            </div>
            <p className="nota-config">
                Textos entre chaves, como <code>{'{ordem}'}</code>, são preenchidos pelo jogo e precisam continuar no texto. Dois
                asteriscos (<code>**assim**</code>) marcam destaque.
                {!termo && alteradosNaTela(marcada) > 0 && ` Nesta tela, ${alteradosNaTela(marcada)} texto(s) diferem do original.`}
            </p>

            <section className="cartao-config" aria-labelledby="textos-bloco">
                <div className="cabecalho-cartao-config">
                    <h3 id="textos-bloco">{tituloDoBloco}</h3>
                </div>
                {visiveis.length === 0 && <p className="nota-config">Nenhum texto encontrado para «{busca.trim()}».</p>}
                {visiveis.map((texto) => {
                    const id = `texto-${texto.chave}`;
                    const restaurando = rascunho[texto.chave] === null && texto.valor !== null;
                    return (
                        <div key={texto.chave} className="campo-texto-config">
                            <label className="campo-config" htmlFor={id}>
                                <span>
                                    {termo ? `${texto.tela} · ${texto.rotulo}` : texto.rotulo}
                                    {texto.valor !== null && <>{' '}<span className="selo-alterado">alterado</span></>}
                                </span>
                            </label>
                            <textarea
                                id={id}
                                className="caixa-texto-config"
                                rows={Math.min(6, Math.max(1, Math.ceil(exibido(texto).length / 90), exibido(texto).split('\n').length))}
                                value={exibido(texto)}
                                disabled={salvando}
                                onChange={(evento) => setRascunho((atual) => ({ ...atual, [texto.chave]: evento.target.value }))}
                            />
                            {texto.variaveis.length > 0 && (
                                <p className="dica-texto">Mantenha: {texto.variaveis.map((variavel) => <code key={variavel}>{variavel}</code>)}</p>
                            )}
                            {texto.valor !== null && (
                                <p className="dica-texto">
                                    {restaurando ? 'Volta ao original ao salvar: ' : 'Original: '}«{texto.padrao}»{' '}
                                    {!restaurando && (
                                        <button
                                            className="botao-texto"
                                            type="button"
                                            disabled={salvando}
                                            onClick={() => setRascunho((atual) => ({ ...atual, [texto.chave]: null }))}
                                        >
                                            Restaurar original
                                        </button>
                                    )}
                                </p>
                            )}
                        </div>
                    );
                })}
            </section>

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={() => setRascunho({})} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
